import { useState } from 'react';
import { Ban, Check, EyeOff, PhoneIncoming, PhoneMissed, Search, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { useMutation, usePaginatedQuery } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useCrm } from './CrmContext';
import { CallButton } from './CallButton';
import { ContactEditor } from './ContactEditor';
import { buttonClass, errorMessage, formatWhen, suggestionFields } from './format';
import { LookupSuggestion, TruecallerButton } from './LookupSuggestion';
import type { CrmCall, CrmContact, NumberLookup } from './types';

type CallerRow = CrmContact & { lastCall: CrmCall | null; lookup?: NumberLookup | null };
type Status = 'new' | 'ignored' | 'spam';

/**
 * Numbers that called (or were called) but are not contacts yet. Naming one makes it a contact
 * with its whole call history; ignoring or marking spam takes it out of the inbox.
 */
export function NewCallersTab() {
  const { data, hasFeature, openContact } = useCrm();
  const [status, setStatus] = useState<Status>('new');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [naming, setNaming] = useState<CallerRow | null>(null);
  const callers = usePaginatedQuery<CallerRow>(backendApi.crm.listNewCallers, { status }, 50);
  const setCallerStatus = useMutation(backendApi.crm.setCallerStatus);
  const ignoreOld = useMutation(backendApi.crm.ignoreOldCallers);
  const convertCaller = useMutation(backendApi.crm.convertCaller);
  const acceptSuggestions = useMutation(backendApi.crm.acceptLookupSuggestions);
  const queueLookups = useMutation(backendApi.crmLookup.queueLookups);
  const canManage = Boolean(data?.canManage);
  const lookupOn = hasFeature('callerLookup') && Boolean(data?.capabilities.lookup);
  const numberOf = (caller: CallerRow) => caller.phones[0]?.number ?? caller.name;
  const confirmedSuggestion = (caller: CallerRow) => caller.lookup?.status === 'found' && caller.lookup.confirmed;

  const lookUp = async (rows: CallerRow[], force = false) => {
    try {
      const result = (await queueLookups({ numbers: rows.map(numberOf), force })) as { queued: number; usedToday: number; dailyLimit: number };
      if (!result.queued) toast('Already looked up');
      else if (result.usedToday >= result.dailyLimit) toast(`${result.queued} queued; today's limit of ${result.dailyLimit} look-ups is used up, so they run tomorrow`);
      else toast.success(`Looking up ${result.queued} number${result.queued === 1 ? '' : 's'}…`);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const applySuggestion = async (caller: CallerRow) => {
    const fields = suggestionFields(caller.lookup, caller.callerIdName);
    if (!fields) return;
    try {
      await convertCaller({ contactId: caller.id, ...fields });
      toast.success(`Saved as ${fields.name}`);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const saveSuggested = async (ids: string[]) => {
    try {
      const result = (await acceptSuggestions({ contactIds: ids })) as { saved: number; skipped: number };
      setSelected(new Set());
      toast.success(`${result.saved} saved under their suggested name${result.skipped ? `; ${result.skipped} without a confirmed suggestion left as they were` : ''}`);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const apply = async (ids: string[], next: Status) => {
    try {
      const result = (await setCallerStatus({ contactIds: ids, status: next })) as { changed: number };
      setSelected(new Set());
      toast.success(
        next === 'spam'
          ? `${result.changed} marked as spam${hasFeature('spamBlocking') ? ' and blocked on the PBX' : ''}`
          : next === 'ignored'
            ? `${result.changed} ignored`
            : `${result.changed} back in New callers`
      );
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const clearBacklog = async () => {
    if (!confirm('Move callers not heard from in 30 days to Ignored? They keep their call history, and you can move any of them back.')) return;
    let total = 0;
    try {
      for (let round = 0; round < 100; round += 1) {
        const result = (await ignoreOld({ olderThanDays: 30 })) as { ignored: number; more: boolean };
        total += result.ignored;
        if (!result.more) break;
      }
      toast.success(`${total.toLocaleString()} older callers moved to Ignored`);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Card animate={false}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="flex gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-700/50 w-fit">
          {(['new', 'ignored', 'spam'] as Status[]).map((value) => (
            <button
              type="button"
              key={value}
              onClick={() => {
                setStatus(value);
                setSelected(new Set());
              }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${status === value ? 'bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}
            >
              {value === 'new' ? 'New' : value === 'ignored' ? 'Ignored' : 'Spam'}
            </button>
          ))}
        </div>
        {canManage && selected.size > 0 && (
          <div className="flex flex-wrap gap-2">
            {lookupOn && status === 'new' && callers.items.some((caller) => selected.has(caller.id) && confirmedSuggestion(caller)) && (
              <button
                type="button"
                onClick={() => void saveSuggested(callers.items.filter((caller) => selected.has(caller.id) && confirmedSuggestion(caller)).map((caller) => caller.id))}
                className={buttonClass.primary}
              >
                <Check className="w-4 h-4" /> Save suggested names ({callers.items.filter((caller) => selected.has(caller.id) && confirmedSuggestion(caller)).length})
              </button>
            )}
            {status !== 'ignored' && (
              <button type="button" onClick={() => void apply(Array.from(selected), 'ignored')} className={buttonClass.secondary}>
                <EyeOff className="w-4 h-4" /> Ignore {selected.size}
              </button>
            )}
            {status !== 'spam' && (
              <button type="button" onClick={() => void apply(Array.from(selected), 'spam')} className={buttonClass.danger}>
                <Ban className="w-4 h-4" /> Spam {selected.size}
              </button>
            )}
            {status !== 'new' && (
              <button type="button" onClick={() => void apply(Array.from(selected), 'new')} className={buttonClass.secondary}>
                Back to New
              </button>
            )}
          </div>
        )}
        {canManage && status === 'new' && (
          <div className="flex flex-wrap gap-2 lg:ml-auto">
            {lookupOn && callers.items.some(confirmedSuggestion) && (
              <button
                type="button"
                onClick={() => setSelected(new Set(callers.items.filter(confirmedSuggestion).map((caller) => caller.id)))}
                className={buttonClass.secondary}
              >
                Select all with a suggestion
              </button>
            )}
            {lookupOn && callers.items.some((caller) => !caller.lookup) && (
              <button type="button" onClick={() => void lookUp(callers.items.filter((caller) => !caller.lookup))} className={buttonClass.secondary}>
                <Search className="w-4 h-4" /> Look up everyone shown
              </button>
            )}
            <button type="button" onClick={() => void clearBacklog()} className={buttonClass.secondary}>
              Ignore callers older than 30 days
            </button>
          </div>
        )}
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {status === 'new'
          ? `Numbers that are not contacts yet, most recent first. Save the ones that matter as contacts; their earlier calls come along.${
              lookupOn ? ' Suggested names come from Google Maps listings and public web pages: check them before saving.' : ''
            }`
          : status === 'spam'
            ? `Marked as spam${hasFeature('spamBlocking') ? ' and blocked on the PBX' : ''}.`
            : 'Ignored numbers keep their call history but stay out of the inbox.'}
      </p>

      {callers.error && <p className="text-red-600 text-sm">{callers.error}</p>}
      {callers.isLoading && <p className="text-gray-500 text-sm">Loading…</p>}
      {!callers.isLoading && callers.items.length === 0 && <p className="text-center py-10 text-gray-500 dark:text-gray-400">Nobody here.</p>}

      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {callers.items.map((caller) => {
          const number = numberOf(caller);
          const suggestion = suggestionFields(caller.lookup, caller.callerIdName);
          const missed = caller.lastCall && caller.lastCall.direction === 'inbound' && !caller.lastCall.answered;
          return (
            <div key={caller.id} className="flex flex-col sm:flex-row sm:items-center gap-3 py-3">
              {canManage && (
                <input
                  type="checkbox"
                  checked={selected.has(caller.id)}
                  onChange={() => toggle(caller.id)}
                  className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
                  aria-label={`Select ${number}`}
                />
              )}
              <button type="button" onClick={() => openContact(caller.id)} className="min-w-0 flex-1 text-left">
                <p className="font-medium text-gray-900 dark:text-white">
                  {number}
                  {caller.callerIdName && (
                    <span className="ml-2 font-normal text-sm text-gray-600 dark:text-gray-300" title="The name that came with the call (caller ID or the PBX phone book)">
                      · caller ID: {caller.callerIdName}
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-3">
                  <span>
                    {caller.stats.totalInteractions} call{caller.stats.totalInteractions === 1 ? '' : 's'}
                    {caller.stats.missedCalls ? `, ${caller.stats.missedCalls} missed` : ''}
                  </span>
                  {caller.lastCall && (
                    <span className="inline-flex items-center gap-1">
                      {missed ? <PhoneMissed className="w-3 h-3 text-red-600" /> : <PhoneIncoming className="w-3 h-3" />}
                      {formatWhen(caller.lastCall.startedAtMs)}
                      {caller.lastCall.answeredByName ? ` · spoke to ${caller.lastCall.answeredByName}` : ''}
                    </span>
                  )}
                  <span>first seen {formatWhen(Date.parse(caller.createdAt))}</span>
                </p>
                {(lookupOn || caller.lookup?.truecaller) && (
                  <div className="mt-1">
                    <LookupSuggestion lookup={caller.lookup} compact />
                  </div>
                )}
              </button>
              <div className="flex gap-1.5">
                <CallButton number={number} size="sm" />
                {canManage && (
                  <>
                    {suggestion && (
                      <button type="button" onClick={() => void applySuggestion(caller)} className={buttonClass.small} title={`Save as ${suggestion.name}`}>
                        <Check className="w-3.5 h-3.5 text-green-600" /> Use
                      </button>
                    )}
                    {lookupOn && (!caller.lookup || caller.lookup.status === 'not_found' || caller.lookup.status === 'error') && (
                      <button
                        type="button"
                        onClick={() => void lookUp([caller], Boolean(caller.lookup))}
                        className={buttonClass.small}
                        title={caller.lookup ? 'Look up again' : 'Look up who this is'}
                      >
                        <Search className="w-3.5 h-3.5" /> {caller.lookup ? 'Again' : 'Look up'}
                      </button>
                    )}
                    <TruecallerButton number={number} lookup={caller.lookup} />
                    <button type="button" onClick={() => setNaming(caller)} className={buttonClass.small}>
                      <UserPlus className="w-3.5 h-3.5" /> Save
                    </button>
                    {status === 'new' && (
                      <>
                        <button type="button" onClick={() => void apply([caller.id], 'ignored')} className={buttonClass.small} title="Ignore">
                          <EyeOff className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" onClick={() => void apply([caller.id], 'spam')} className={buttonClass.small} title="Spam">
                          <Ban className="w-3.5 h-3.5 text-red-600" />
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {!callers.isDone && callers.items.length > 0 && (
        <button type="button" onClick={callers.loadMore} disabled={callers.loadingMore} className={`${buttonClass.secondary} mt-4`}>
          {callers.loadingMore ? 'Loading…' : 'Load more'}
        </button>
      )}
      {naming && (
        <ContactEditor contact={naming} suggestion={lookupOn ? suggestionFields(naming.lookup) : null} onClose={() => setNaming(null)} />
      )}
    </Card>
  );
}
